import type { TargetingOptionValue } from '@hotjar/heatmaps';
import {
  SIMPLE_MATCH_AUTOCOMPLETE_PLACEHOLDER,
  TARGETING_OPTION_VALUE,
  TARGETING_PLACEHOLDERS,
  TARGETING_VALUE,
} from '@hotjar/heatmaps';
import { validateUrl } from '@hotjar/insights-common/lib/url';
import { useTranslation } from 'react-i18next';

interface UseSearchBarHook {
  getPlaceholder: (
    targetValue: TargetingValue,
    areSuggestionsHidden?: boolean,
  ) =>
    | typeof SIMPLE_MATCH_AUTOCOMPLETE_PLACEHOLDER
    | (typeof TARGETING_PLACEHOLDERS)[keyof typeof TARGETING_PLACEHOLDERS];
  getTargetingValueByTargetingOption: (option: TargetingOptionValue) => TargetingValue;
  shouldHideUrlSuggestions: boolean;
  validateSelectedUrl: (url: string) => string;
}

const ERROR_MESSAGES = {
  URL_REQUIRED: 'heatmaps:searchbar.url-required',
  INVALID_URL_PROTOCOL_REQUIRED: 'heatmaps:searchbar.invalid-url-protocol-required',
  INVALID_URL_PROTOCOL_OPTIONAL: 'heatmaps:searchbar.invalid-url-protocol-optional',
  INVALID_CONTAINS_URL: 'heatmaps:searchbar.invalid-contains-url',
  INVALID_REGEX: 'heatmaps:searchbar.invalid-regex',
};

const getTargetingValueByTargetingOption = (
  targetingOption: TargetingOptionValue,
): TargetingValue => {
  if (targetingOption === TARGETING_OPTION_VALUE.SINGLE_PAGE) {
    return TARGETING_VALUE.SIMPLE;
  }

  return targetingOption;
};

const isRegexValid = (input: string) => {
  try {
    RegExp(input);
    return true;
  } catch (e) {
    return false;
  }
};

export function useSearchBar(selectedTargetingOption: TargetingOptionValue): UseSearchBarHook {
  const { t } = useTranslation();

  const getPlaceholder = (targetValue: TargetingValue, areSuggestionsHidden?: boolean) => {
    if (targetValue === TARGETING_VALUE.SIMPLE && !areSuggestionsHidden) {
      return t(SIMPLE_MATCH_AUTOCOMPLETE_PLACEHOLDER);
    }

    return t(TARGETING_PLACEHOLDERS[targetValue]);
  };

  const shouldHideUrlSuggestions =
    selectedTargetingOption !== TARGETING_OPTION_VALUE.SINGLE_PAGE &&
    selectedTargetingOption !== TARGETING_OPTION_VALUE.EXACT;

  const validateSelectedUrl = (url: string) => {
    let errorMessage;

    if (!url) {
      errorMessage = t(ERROR_MESSAGES.URL_REQUIRED);
    } else if (
      (selectedTargetingOption === TARGETING_OPTION_VALUE.EXACT ||
        selectedTargetingOption === TARGETING_VALUE.STARTS_WITH) &&
      !validateUrl(url, true)
    ) {
      errorMessage = t(ERROR_MESSAGES.INVALID_URL_PROTOCOL_REQUIRED);
    } else if (
      selectedTargetingOption === TARGETING_OPTION_VALUE.SINGLE_PAGE &&
      !validateUrl(url, false)
    ) {
      errorMessage = t(ERROR_MESSAGES.INVALID_URL_PROTOCOL_OPTIONAL);
    } else if (selectedTargetingOption === TARGETING_VALUE.CONTAINS && url.length < 3) {
      errorMessage = t(ERROR_MESSAGES.INVALID_CONTAINS_URL);
    } else if (selectedTargetingOption === TARGETING_VALUE.REGEX && !isRegexValid(url)) {
      errorMessage = t(ERROR_MESSAGES.INVALID_REGEX);
    } else {
      errorMessage = '';
    }

    return errorMessage;
  };

  return {
    getTargetingValueByTargetingOption,
    getPlaceholder,
    shouldHideUrlSuggestions,
    validateSelectedUrl,
  };
}
